/**
 * Compliance & ERA Assessment page.
 *
 * Three sections:
 *   1. Compliance Rules — view/edit the active rule set, upload rules from files,
 *      add/delete individual rules.
 *   2. Generate ERA — select a client document, optionally provide extra rules
 *      via chat prompt, then generate the report.
 *   3. Previous Reports — past ERA reports table.
 *
 * Themed with KPMG Dark Green (#098E7E).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Typography, Box, Paper, Button, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, MenuItem, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, LinearProgress, Accordion, AccordionSummary,
  AccordionDetails, Stack, IconButton, Tabs, Tab, Tooltip,
} from '@mui/material';
import {
  Security, ExpandMore, CheckCircle, Error as ErrorIcon,
  HelpOutline, PlayArrow, Save, Upload, Add, Delete, Edit, Chat, Download,
} from '@mui/icons-material';
import {
  getComplianceRules, saveComplianceRules, uploadComplianceRulesFile,
  generateEra, generateEraFromFiles, downloadEraReport, listEraReports, listClientUploadsForEra,
  ComplianceRule, EraResult, EraReportItem, ClientUploadItem,
} from '../../../api/era';
import RefreshButton from '../../../components/RefreshButton';
import PortalTooltip from '../../../components/PortalTooltip';

const PORTAL_COLOR = '#098E7E';

/* ── Finding badge helpers ─────────────────────────────────────── */

const FINDING_COLORS: Record<string, 'success' | 'error' | 'warning'> = {
  compliant: 'success',
  non_compliant: 'error',
  needs_review: 'warning',
};

const FINDING_ICONS: Record<string, React.ReactNode> = {
  compliant: <CheckCircle fontSize="small" />,
  non_compliant: <ErrorIcon fontSize="small" />,
  needs_review: <HelpOutline fontSize="small" />,
};

const SEVERITY_COLORS: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'default',
};


const qualityColor = (value?: string | null): string => {
  const normalized = (value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
  if (normalized === 'good' || normalized === 'high') return '#2e7d32';
  if (normalized === 'medium' || normalized === 'fair') return '#0288d1';
  if (
    normalized === 'needs review'
    || normalized === 'needs-review'
    || normalized === 'low'
    || normalized === 'critical'
    || normalized === 'poor'
  ) return '#d32f2f';
  return '#888';
};

const SEVERITY_OPTIONS = ['critical', 'high', 'medium', 'low'];
const CHECK_TYPE_OPTIONS = ['binary', 'qualitative'];
const CATEGORIES = [
  'Independence', 'Ethics', 'Confidentiality', 'Engagement',
  'Audit Execution', 'Communication', 'Prohibited Activities',
  'Quality Control', 'Legal Compliance', 'Improvement', 'Custom',
];

/* ── Main component ────────────────────────────────────────────── */

const EraGenerator: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);

  // Compliance rules state
  const [rules, setRules] = useState<ComplianceRule[]>([]);
  const [ruleSetName, setRuleSetName] = useState('');
  const [ruleSetSource, setRuleSetSource] = useState('');
  const [rulesUpdatedAt, setRulesUpdatedAt] = useState<string | null>(null);
  const [rulesDirty, setRulesDirty] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [uploadingRules, setUploadingRules] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Editing a single rule
  const [editingRule, setEditingRule] = useState<ComplianceRule | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Client uploads & generation
  const [clientUploads, setClientUploads] = useState<ClientUploadItem[]>([]);
  const [selectedUploadId, setSelectedUploadId] = useState('');
  const [chatRules, setChatRules] = useState('');
  const [extraContext, setExtraContext] = useState('');
  const [proposalFiles, setProposalFiles] = useState<File[]>([]);
  const [rulesDocumentFile, setRulesDocumentFile] = useState<File | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generatingProgress, setGeneratingProgress] = useState('');
  const [eraResult, setEraResult] = useState<EraResult | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [lastGeneratedReportId, setLastGeneratedReportId] = useState<string | null>(null);
  const [reportNameDialogOpen, setReportNameDialogOpen] = useState(false);
  const reportNameInputRef = useRef<HTMLInputElement>(null);
  const [reportNameError, setReportNameError] = useState('');

  // Reports
  const [reports, setReports] = useState<EraReportItem[]>([]);

  // Misc
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /* ── Data loading ───────────────────────────────────────────── */

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, uploads, reps] = await Promise.all([
        getComplianceRules(),
        listClientUploadsForEra(),
        listEraReports(),
      ]);
      setRules(rulesRes.rule_set.rules);
      setRuleSetName(rulesRes.rule_set.name);
      setRuleSetSource(rulesRes.rule_set.source);
      setRulesUpdatedAt(rulesRes.rule_set.updated_at);
      setRulesDirty(false);
      setClientUploads(uploads);
      setReports(reps);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
      setLastRefreshed(new Date());
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  /* ── Rules editing ──────────────────────────────────────────── */

  const handleAddRule = () => {
    const nextId = `RULE-${String(rules.length + 1).padStart(3, '0')}`;
    setEditingRule({
      id: nextId,
      category: 'Custom',
      title: '',
      rule: '',
      severity: 'medium',
      check_type: 'qualitative',
      guidance: '',
    });
    setEditDialogOpen(true);
  };

  const handleEditRule = (index: number) => {
    setEditingRule({ ...rules[index] });
    setEditDialogOpen(true);
  };

  const handleSaveEditedRule = () => {
    if (!editingRule) return;
    const idx = rules.findIndex((r) => r.id === editingRule.id);
    if (idx >= 0) {
      const updated = [...rules];
      updated[idx] = editingRule;
      setRules(updated);
    } else {
      setRules([...rules, editingRule]);
    }
    setRulesDirty(true);
    setEditDialogOpen(false);
    setEditingRule(null);
  };

  const handleDeleteRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
    setRulesDirty(true);
  };

  const handleSaveRules = async () => {
    setSavingRules(true);
    setError(null);
    try {
      const res = await saveComplianceRules(rules, ruleSetName);
      setSuccess(res.message);
      setRulesDirty(false);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to save rules');
    } finally {
      setSavingRules(false);
    }
  };

  const handleUploadRules = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingRules(true);
    setError(null);
    try {
      const res = await uploadComplianceRulesFile(file);
      if (res.rules && res.rules.length > 0) {
        setRules(res.rules);
        setRulesDirty(false);
        setSuccess(res.message);
      } else if (res.raw_text) {
        setSuccess(res.message);
        setError('Rules could not be auto-structured. Try pasting key rules into the chat prompt instead.');
      }
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploadingRules(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /* ── ERA generation ─────────────────────────────────────────── */

  const handleGenerate = () => {
    const hasUploadedProposalFiles = proposalFiles.length > 0;
    if (!hasUploadedProposalFiles && !selectedUploadId) {
      setError('Select a client document or upload one or more proposal files');
      return;
    }
    setReportNameError('');
    if (reportNameInputRef.current) reportNameInputRef.current.value = '';
    setReportNameDialogOpen(true);
  };

  const handleConfirmGenerate = async () => {
    const cleanReportName = reportNameInputRef.current?.value.trim() || '';
    if (!cleanReportName) {
      setReportNameError('Please enter the name of the file');
      return;
    }
    const hasUploadedProposalFiles = proposalFiles.length > 0;
    setReportNameDialogOpen(false);
    setGenerating(true);
    setGeneratingProgress('Submitting ERA request...');
    setError(null);
    setEraResult(null);
    try {
      const onProgress = (msg: string) => setGeneratingProgress(msg);
      const res = hasUploadedProposalFiles
        ? await generateEraFromFiles(proposalFiles, {
            chatRules: chatRules || undefined,
            context: extraContext || undefined,
            rulesFile: rulesDocumentFile || undefined,
            reportName: cleanReportName,
          } as any, onProgress)
        : await generateEra(selectedUploadId, {
            chatRules: chatRules || undefined,
            context: extraContext || undefined,
            reportName: cleanReportName,
          } as any, onProgress);

      setEraResult(res.era_result);
      setLastGeneratedReportId(res.report_id);
      setSuccess('ERA report generated successfully');
      setDetailOpen(true);
      setProposalFiles([]);
      refresh();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || 'ERA generation failed');
    } finally {
      setGenerating(false);
      setGeneratingProgress('');
    }
  };

  const handleDownloadReport = async (reportId: string, fallbackFileName?: string) => {
    try {
      const blob = await downloadEraReport(reportId);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fallbackFileName || `ERA_Report_${reportId.slice(0, 8)}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download ERA report');
    }
  };

  /* ── Render ─────────────────────────────────────────────────── */

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <PortalTooltip tooltipKey="domain_management.page.era_generator" showIcon>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: PORTAL_COLOR }}>
              Compliance & ERA Assessment
            </Typography>
          </PortalTooltip>
          <Typography variant="body2" color="text.secondary">
            Manage compliance rules, then generate Engagement Risk Assessment reports by comparing client documents against them.
          </Typography>
        </Box>
        <RefreshButton onRefresh={refresh} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        sx={{ mb: 2, '& .Mui-selected': { color: PORTAL_COLOR }, '& .MuiTabs-indicator': { backgroundColor: PORTAL_COLOR } }}
      >
        <Tab label={
          <Tooltip title="View, edit, add, and delete compliance rules. Upload rules from JSON, DOCX, PDF, or Excel files." arrow>
            <span>{`Compliance Rules (${rules.length})`}</span>
          </Tooltip>
        } />
        <Tab label={
          <Tooltip title="Select a client document and generate an Engagement Risk Assessment report. The AI agent compares it against all active compliance rules." arrow>
            <span>Generate ERA Report</span>
          </Tooltip>
        } />
        <Tab label={
          <Tooltip title="View past ERA reports with quality ratings, findings, and expiry dates." arrow>
            <span>{`Previous Reports (${reports.length})`}</span>
          </Tooltip>
        } />
      </Tabs>

      {/* ═══ TAB 0: COMPLIANCE RULES ═══ */}
      {tabIndex === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <PortalTooltip tooltipKey="era.compliance_rules.header" showIcon>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Security sx={{ color: PORTAL_COLOR }} />
                  {ruleSetName || 'Compliance Rules'}
                </Typography>
              </PortalTooltip>
              <Typography variant="caption" color="text.secondary">
                Source: {ruleSetSource}
                {rulesUpdatedAt && ` · Last updated: ${new Date(rulesUpdatedAt).toLocaleString()}`}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Add a new compliance rule manually. Rules are checked against client documents during ERA generation." arrow>
                <Button size="small" variant="outlined" startIcon={<Add />} onClick={handleAddRule}>
                  Add Rule
                </Button>
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".json,.docx,.pdf,.pptx,.xlsx,.xls,.txt,.csv"
                onChange={handleUploadRules}
              />
              <Tooltip title="Upload a compliance rules file (.json, .docx, .pdf, .pptx, .xlsx, .xls, .txt, .csv). JSON files are loaded directly; other formats are parsed and structured by the AI agent." arrow>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={uploadingRules ? <CircularProgress size={16} /> : <Upload />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingRules}
                  >
                    Upload Rules File
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Save all rule changes to the database. Rules must be saved before they are used in ERA generation." arrow>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={savingRules ? <CircularProgress size={16} color="inherit" /> : <Save />}
                    onClick={handleSaveRules}
                    disabled={!rulesDirty || savingRules}
                    sx={{ backgroundColor: PORTAL_COLOR, '&:hover': { backgroundColor: '#077a6d' } }}
                  >
                    Save Rules
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          {rulesDirty && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You have unsaved changes. Click <strong>Save Rules</strong> to persist them.
            </Alert>
          )}

          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 90 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 130 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Rule</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 90 }}>Severity</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 80 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 90 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule, idx) => (
                  <TableRow key={rule.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{rule.id}</Typography>
                    </TableCell>
                    <TableCell>{rule.category}</TableCell>
                    <TableCell>{rule.title}</TableCell>
                    <TableCell>
                      <Tooltip title={rule.rule}>
                        <Typography variant="body2" sx={{ fontSize: 12, maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {rule.rule}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip label={rule.severity} size="small" color={SEVERITY_COLORS[rule.severity] || 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={rule.check_type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit rule">
                        <IconButton size="small" onClick={() => handleEditRule(idx)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete rule">
                        <IconButton size="small" color="error" onClick={() => handleDeleteRule(idx)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {rules.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No compliance rules loaded. Upload a file or add rules manually.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Supported upload formats: <strong>.json, .docx, .pdf, .xlsx, .txt</strong>.
              JSON files are loaded directly. Other formats are parsed and auto-structured by the AI agent.
            </Typography>
          </Box>
        </Paper>
      )}

      {/* ═══ TAB 1: GENERATE ERA ═══ */}
      {tabIndex === 1 && (
        <Paper sx={{ p: 3 }}>
          <PortalTooltip tooltipKey="era.generate.header" showIcon>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlayArrow sx={{ color: PORTAL_COLOR }} /> Generate ERA Report
            </Typography>
          </PortalTooltip>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The agent will compare one or more proposal files against the current compliance rules
            ({rules.length} rules loaded) and return a structured risk assessment.
          </Typography>

          <Stack spacing={2}>
            <TextField
              select
              fullWidth
              label="Select Existing Client Document (optional)"
              value={selectedUploadId}
              onChange={(e) => setSelectedUploadId(e.target.value)}
              helperText="Use this OR upload new proposal files below. If proposal files are uploaded, they are used for analysis."
            >
              {clientUploads.length === 0 ? (
                <MenuItem disabled value="">No client uploads available</MenuItem>
              ) : (
                clientUploads.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.filename} — {u.assessment_type} (by {u.uploaded_by_name || 'Unknown'}, {u.status})
                  </MenuItem>
                ))
              )}
            </TextField>

            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<Upload />}
              >
                Upload Proposal Files (Multiple)
                <input
                  hidden
                  type="file"
                  multiple
                  accept=".json,.docx,.pdf,.pptx,.xlsx,.xls,.txt,.csv,.md"
                  onChange={(e) => setProposalFiles(Array.from(e.target.files || []))}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {proposalFiles.length > 0
                  ? `${proposalFiles.length} file(s) selected: ${proposalFiles.map((f) => f.name).join(', ')}`
                  : 'No local proposal files selected'}
              </Typography>
            </Box>

            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<Security />}
              >
                Upload Rules Document (Optional)
                <input
                  hidden
                  type="file"
                  accept=".json,.docx,.pdf,.pptx,.xlsx,.xls,.txt,.csv,.md"
                  onChange={(e) => setRulesDocumentFile(e.target.files?.[0] || null)}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {rulesDocumentFile
                  ? `Rules document selected: ${rulesDocumentFile.name}`
                  : 'Uses active saved rules when no rules document is uploaded'}
              </Typography>
            </Box>

            <Tooltip title="Type additional rules or instructions here. These are sent to the AI agent alongside the saved rules — useful for engagement-specific requirements." arrow placement="top-start">
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Additional compliance rules / instructions for the agent (optional)"
                placeholder="e.g. Also check that the proposal includes data retention policy, GDPR provisions, and third-party vendor assessment requirements..."
                value={chatRules}
                onChange={(e) => setChatRules(e.target.value)}
                InputProps={{
                  startAdornment: <Chat sx={{ color: 'text.secondary', mr: 1, mt: 1, alignSelf: 'flex-start' }} />,
                }}
                helperText="Provide any additional compliance requirements in plain text. The agent will use these alongside the saved rules."
              />
            </Tooltip>

            <Tooltip title="Provide engagement context so the AI agent can tailor its analysis. E.g., industry sector, engagement type, or client-specific considerations." arrow placement="top-start">
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Engagement context (optional)"
                placeholder="e.g. This is a tax advisory engagement for a public sector client..."
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
              />
            </Tooltip>

            <Box>
              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                onClick={handleGenerate}
                disabled={generating || (!selectedUploadId && proposalFiles.length === 0)}
                sx={{ backgroundColor: PORTAL_COLOR, '&:hover': { backgroundColor: '#077a6d' } }}
              >
                {generating ? 'Generating ERA Report...' : 'Generate ERA Report'}
              </Button>
            </Box>

            {generating && (
              <Box>
                <LinearProgress sx={{ mt: 1, '& .MuiLinearProgress-bar': { backgroundColor: PORTAL_COLOR } }} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {generatingProgress || `AI agent is comparing the document against ${rules.length} compliance rules. This may take a few minutes…`}
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      {/* ═══ TAB 2: PREVIOUS REPORTS ═══ */}
      {tabIndex === 2 && (
        <>
          {reports.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No ERA reports generated yet.</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Report Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Quality Rating</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Findings</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Download</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((r) => (
                    <TableRow key={r.report_id}>
                      <TableCell>{(r as any).report_name || r.file_name || '—'}</TableCell>
                      <TableCell>{r.domain}</TableCell>
                      <TableCell>{r.category}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.quality_rating}
                          size="small"
                          sx={{ backgroundColor: qualityColor(r.quality_rating), color: '#fff', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>{r.findings_count} rules checked</TableCell>
                      <TableCell>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>
                        <Tooltip title="Download ERA report Excel">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadReport(r.report_id, r.file_name || undefined)}
                            >
                              <Download fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ═══ EDIT RULE DIALOG ═══ */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRule && rules.find((r) => r.id === editingRule.id) ? 'Edit' : 'Add'} Compliance Rule
        </DialogTitle>
        <DialogContent>
          {editingRule && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Rule ID"
                fullWidth
                value={editingRule.id}
                onChange={(e) => setEditingRule({ ...editingRule, id: e.target.value })}
              />
              <TextField
                select
                label="Category"
                fullWidth
                value={editingRule.category}
                onChange={(e) => setEditingRule({ ...editingRule, category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Title"
                fullWidth
                value={editingRule.title}
                onChange={(e) => setEditingRule({ ...editingRule, title: e.target.value })}
              />
              <TextField
                label="Rule Text"
                fullWidth
                multiline
                rows={3}
                value={editingRule.rule}
                onChange={(e) => setEditingRule({ ...editingRule, rule: e.target.value })}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  select
                  label="Severity"
                  fullWidth
                  value={editingRule.severity}
                  onChange={(e) => setEditingRule({ ...editingRule, severity: e.target.value })}
                >
                  {SEVERITY_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Check Type"
                  fullWidth
                  value={editingRule.check_type}
                  onChange={(e) => setEditingRule({ ...editingRule, check_type: e.target.value })}
                >
                  {CHECK_TYPE_OPTIONS.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                label="Guidance"
                fullWidth
                multiline
                rows={2}
                value={editingRule.guidance}
                onChange={(e) => setEditingRule({ ...editingRule, guidance: e.target.value })}
                helperText="What should the agent look for when checking this rule?"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setEditingRule(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEditedRule}
            disabled={!editingRule?.id || !editingRule?.rule}
            sx={{ backgroundColor: PORTAL_COLOR, '&:hover': { backgroundColor: '#077a6d' } }}
          >
            Save Rule
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reportNameDialogOpen} onClose={() => setReportNameDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Name the ERA report file</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="File name"
            defaultValue=""
            inputRef={reportNameInputRef}
            error={Boolean(reportNameError)}
            helperText={reportNameError || 'The .xlsx extension will be added automatically.'}
            onKeyDown={(event) => { if (event.key === 'Enter') void handleConfirmGenerate(); }}
            inputProps={{ maxLength: 180 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportNameDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleConfirmGenerate()} sx={{ backgroundColor: PORTAL_COLOR }}>Generate ERA</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ ERA RESULT DETAIL DIALOG ═══ */}
      <Dialog open={detailOpen && !!eraResult} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Security />
          ERA Report Results
          {eraResult && (
            <Chip
              label={eraResult.quality_rating}
              size="small"
              sx={{ ml: 'auto', backgroundColor: qualityColor(eraResult.quality_rating), color: '#fff', fontWeight: 600 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {eraResult && (
            <Box>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: '#f9f9f9' }}>
                <Typography variant="subtitle2" gutterBottom>Executive Summary</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>{eraResult.executive_summary}</Typography>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" spacing={3} sx={{ mt: 1 }} flexWrap="wrap">
                  <Chip label={`${eraResult.total_rules_checked} rules checked`} size="small" variant="outlined" />
                  <Chip label={`${eraResult.compliant_count} compliant`} size="small" color="success" />
                  <Chip label={`${eraResult.non_compliant_count} non-compliant`} size="small" color="error" />
                  <Chip label={`${eraResult.needs_review_count} needs review`} size="small" color="warning" />
                </Stack>
              </Paper>

              {eraResult.recommendations.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Priority Recommendations</Typography>
                  <ol style={{ margin: 0, paddingLeft: 20 }}>
                    {eraResult.recommendations.map((rec, i) => (
                      <li key={i}><Typography variant="body2">{rec}</Typography></li>
                    ))}
                  </ol>
                </Paper>
              )}

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Detailed Findings</Typography>
              {eraResult.era_findings.map((finding, idx) => (
                <Accordion key={idx} defaultExpanded={finding.finding === 'non_compliant'}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {FINDING_ICONS[finding.finding]}
                      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                        {finding.rule_id} — {finding.title}
                      </Typography>
                      <Chip
                        label={finding.finding.replace('_', ' ')}
                        size="small"
                        color={FINDING_COLORS[finding.finding] || 'default'}
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={finding.severity}
                        size="small"
                        color={SEVERITY_COLORS[finding.severity] || 'default'}
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Category</Typography>
                        <Typography variant="body2">{finding.category}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Evidence</Typography>
                        <Typography variant="body2">{finding.evidence}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Recommendation</Typography>
                        <Typography variant="body2">{finding.recommendation}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Risk Score</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(finding.risk_score / 5) * 100}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            mt: 0.5,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: finding.risk_score >= 4 ? '#d32f2f' : finding.risk_score >= 3 ? '#ed6c02' : '#2e7d32',
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">{finding.risk_score}/5</Typography>
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {lastGeneratedReportId && (
            <Button
              startIcon={<Download />}
              onClick={() => handleDownloadReport(lastGeneratedReportId, undefined)}
            >
              Download JSON
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EraGenerator;
